/**
 * DeviceEnrollBanner — Non-blocking fixed banner for device enrollment outcomes.
 *
 * Design: enroll-device-on-login §7 (Error mapping), §2 (Banner surfaces via DeviceEnrollContext)
 * Visual: matches Clock_Web.html notification/alert style exactly.
 *
 * Rules (non-negotiable per myintelli-clock-hex skill):
 *  - useTheme() → { T, C } for ALL colors — NO hardcoded hex
 *  - Inline style={{}} for EVERY color, shadow, border
 *  - Animation: mi-anim-slide-up from src/index.css
 *  - Border radius: 10–14px for card, 999px for close pill
 *  - Alpha colors: inline hex e.g. `${C.danger}38`
 */
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { useDeviceEnrollState } from '@/modules/devices/presentation/context/useDeviceEnrollState';

export function DeviceEnrollBanner() {
  const { t } = useTranslation('common');
  const { C, T } = useTheme();
  const { status, errorKind, errorDetail, dismiss } = useDeviceEnrollState();

  // Only render on error
  if (status !== 'error') return null;

  const isAlreadyRegistered = errorKind === 'already-registered';
  const isLicenseExceeded = errorKind === 'license-exceeded';
  const isTimezoneRejected = errorKind === 'timezone-rejected';
  const accentColor = isAlreadyRegistered ? C.blue : C.danger;

  let copy = t('device.bannerEnrollFailed');
  if (isAlreadyRegistered) {
    copy = t('device.bannerAlreadyRegistered');
  } else if (isLicenseExceeded) {
    copy = t('device.bannerLicenseExceeded');
  } else if (isTimezoneRejected) {
    copy = t('device.bannerTimezoneRejected', { timezone: errorDetail ?? '—' });
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 16,
        left: 0,
        right: 0,
        zIndex: 9999,
        display: 'flex',
        justifyContent: 'center',
        padding: '0 16px',
        pointerEvents: 'none',
      }}
    >
    <div
      role="alert"
      aria-live="assertive"
      className="mi-anim-slide-up"
      style={{
        maxWidth: 520,
        width: '100%',
        background: T.surfaceBg,
        border: `1px solid ${accentColor}38`,
        borderLeft: `3px solid ${accentColor}`,
        borderRadius: 12,
        boxShadow: T.shadow,
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontFamily: 'inherit',
        pointerEvents: 'auto',
      }}
    >
      {/* Left icon */}
      <div
        style={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: `${accentColor}14`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isAlreadyRegistered ? (
          // Info icon
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={accentColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        ) : (
          // Warning icon
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={accentColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        )}
      </div>

      {/* Copy */}
      <span
        style={{
          flex: 1,
          fontSize: 13,
          fontWeight: 500,
          color: T.text,
          lineHeight: 1.4,
        }}
      >
        {copy}
      </span>

      {/* Dismiss button */}
      <button
        type="button"
        aria-label="Cerrar"
        onClick={dismiss}
        style={{
          flexShrink: 0,
          width: 24,
          height: 24,
          borderRadius: '50%',
          border: 'none',
          background: `${T.textMuted}22`,
          cursor: 'pointer',
          color: T.textMuted,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          transition: 'background .15s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = `${accentColor}22`; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = `${T.textMuted}22`; }}
      >
        <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
    </div>
  );
}
